import { getActiveLocale, localeTag } from "./i18n";

/**
 * Dates and numbers (HANDOFF §7): "Fri 10 Oct", "Fri 10 Oct · 09:14" (24-hour), relative times in the active
 * UI language. All functions take an optional `locale` ("en" | "da" | BCP 47); without it the locale of the
 * current `LocaleProvider` is used, never the browser locale.
 */
const parse = (v?: string | null): Date | null => { if (!v) return null; const d = new Date(v); return isNaN(d.getTime()) ? null : d; };

/** "Fri 10 Oct" (year added when it is not the current year). `opts` overrides the parts. */
export function fmtDate(v?: string | null, opts?: Intl.DateTimeFormatOptions, locale?: string): string {
  if (!v) return "";
  const d = parse(v); if (!d) return String(v);
  const o: Intl.DateTimeFormatOptions = opts ?? { weekday: "short", day: "numeric", month: "short", year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric" };
  return new Intl.DateTimeFormat(localeTag(locale ?? getActiveLocale()), o).format(d);
}
/** "09:14" (24-hour). `timeZone` (IANA) shows the time in that zone instead of the browser's. */
export function fmtTime(v?: string | null, locale?: string, timeZone?: string): string {
  const d = parse(v); if (!d) return v ? String(v) : "";
  return new Intl.DateTimeFormat(localeTag(locale ?? getActiveLocale()), { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone }).format(d);
}
/** "Fri 10 Oct · 09:14". `timeZone` (IANA) shows the instant in that zone instead of the browser's. */
export function fmtDateTime(v?: string | null, locale?: string, timeZone?: string): string {
  if (!v) return "";
  const d = parse(v); if (!d) return String(v);
  const date = timeZone ? fmtDate(v, { weekday: "short", day: "numeric", month: "short", year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric", timeZone }, locale) : fmtDate(v, undefined, locale);
  return `${date} · ${fmtTime(v, locale, timeZone)}`;
}
/** Relative time for past AND future instants ("3 hours ago", "in 12 days"; "for 3 timer siden" in Danish). */
export function timeAgo(v?: string | null, locale?: string): string {
  if (!v) return "";
  const diff = (new Date(v).getTime() - Date.now()) / 1000;   // negative = past
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat(localeTag(locale ?? getActiveLocale()), { numeric: "auto" });
  if (abs < 60) return rtf.format(Math.round(diff), "second");
  if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
  if (abs < 86400 * 30) return rtf.format(Math.round(diff / 86400), "day");
  return rtf.format(Math.round(diff / (86400 * 30)), "month");
}
/** Alias of `timeAgo` (portal naming). */
export const relTime = timeAgo;
export function fmtNumber(n?: number | null, locale?: string): string { return n === undefined || n === null ? "" : new Intl.NumberFormat(localeTag(locale ?? getActiveLocale())).format(n); }
export function initials(name?: string): string { return (name ?? "").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]!.toUpperCase()).join(""); }
